import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Icon } from '../Icon'; // Припускається, що у вас є цей компонент
import { useTranslation } from 'react-i18next'; // Припускається, що у вас є цей хук
dayjs.extend(customParseFormat);

const DatePicker = (props) => {
  const { t } = useTranslation();

  const [inputValue, setInputValue] = useState('');
  const [inputTime, setInputTime] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(dayjs());

  const inputRef = useRef(null);
  const timeRef = useRef(null);
  const calendarRef = useRef(null);
  const iconRef = useRef(null);

  // Оновлюємо відображуваний місяць при зміні selectedDate
  useEffect(() => {
    if (selectedDate) {
      setCurrentMonth(selectedDate.startOf('month'));
    }
  }, [selectedDate]);

  // Обробка кліку поза календарем
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current && !inputRef.current.contains(event.target) &&
        timeRef.current && !timeRef.current.contains(event.target) &&
        calendarRef.current && !calendarRef.current.contains(event.target) &&
        iconRef.current && !iconRef.current.contains(event.target)
      ) {
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleCalendar = () => {
    setIsCalendarOpen((prev) => !prev);
  };

  const formatInputValue = (value) => {
    const cleanedValue = value.replace(/\D/g, '');
    let formattedValue = '';

    if (cleanedValue.length > 0) {
      let day = cleanedValue.substring(0, 2);
      if (parseInt(day) > 31) day = '31';
      formattedValue += day;
    }
    if (cleanedValue.length > 2) {
      let month = cleanedValue.substring(2, 4);
      if (parseInt(month) > 12) month = '12';
      formattedValue += '.' + month;
    }
    if (cleanedValue.length > 4) {
      formattedValue += '.' + cleanedValue.substring(4, 8);
    }

    return formattedValue;
  };

  const formatTime = (value) => {
    const cleaned = value.replace(/\D/g, '');
    let result = '';

    if (cleaned.length > 0) {
      let hh = cleaned.substring(0, 2);
      if (parseInt(hh) > 23) hh = '23';
      result += hh;
    }
    if (cleaned.length > 2) {
      let mm = cleaned.substring(2, 4);
      if (parseInt(mm) > 59) mm = '59';
      result += ':' + mm;
    }

    return result;
  };

  useEffect(() => {
    if (props.value?.length > 0) {
      const parsedFormat = props.time ? 'DD.MM.YYYY HH:mm' : 'DD.MM.YYYY';
      const parsed = dayjs(props.value, parsedFormat, true);
      
      if (parsed.isValid()) {
        const datePart = parsed.format('DD.MM.YYYY');
        const timePart = props.time ? parsed.format('HH:mm') : '';

        setInputValue(datePart);
        setInputTime(timePart);
        setSelectedDate(parsed);
      } else {
        // Якщо props.value не валідний, очищуємо
        setInputValue('');
        setInputTime('');
        setSelectedDate(null);
      }
    } else {
      setInputValue('');
      setInputTime('');
      setSelectedDate(null);
    }
  }, [props.value, props.time]);


  const validateAndSetDate = (value) => {
    const parsedDate = dayjs(value, 'DD.MM.YYYY', true);
    if (!parsedDate.isValid()) return;

    let finalDate = parsedDate;

    // Зберігаємо час, якщо він уже встановлений
    if (props.time && selectedDate) {
        // Використовуємо час з існуючої дати, щоб зберегти його
        finalDate = finalDate.hour(selectedDate.hour()).minute(selectedDate.minute());
    } else if (props.time && inputTime.length === 5) {
      // Використовуємо час з поля вводу часу
      const [hh, mm] = inputTime.split(':');
      finalDate = finalDate.hour(hh).minute(mm);
    } else if (!props.time) {
        finalDate = finalDate.startOf('day'); // Обнуляємо час, якщо його не треба
    }
    setSelectedDate(finalDate);
    setCurrentMonth(finalDate.startOf('month'));
    // Оновлення props.onChange при валідному введенні
    const formatted = props.time
        ? finalDate.format('DD.MM.YYYY HH:mm')
        : finalDate.format('DD.MM.YYYY');
      if(formatted == props.value){
        return;
      }else{
        props.onChange?.({
          target: { id: props.id, value: formatted }
        });
      }
  };

  const handleInputChange = (e) => {
    const formatted = formatInputValue(e.target.value);
    setInputValue(formatted);

    // Валідація при повному вводі (для режиму без часу)
    if (formatted.length === 10 && !props.time) {
        validateAndSetDate(formatted);
    }
  };

  const handleTimeChange = (e) => {
    const formatted = formatTime(e.target.value);
    setInputTime(formatted);

    // Оновлюємо час, лише якщо є валідна дата для застосування часу
    if (formatted.length === 5 && selectedDate) {
      const [hh, mm] = formatted.split(':');
      const newDate = selectedDate.clone().hour(hh).minute(mm);
      if(newDate.format('DD.MM.YYYY HH:mm') == props.value && props.time){
        return;
      }else if(newDate.format('DD.MM.YYYY') == props.value && !props.time){
        return;
      }else{
        setSelectedDate(newDate);
        props.onChange?.({
          target: { id: props.id, value: newDate.format('DD.MM.YYYY HH:mm') }
        });
      }
    }
  };

  const handleDateSelect = (date) => {
    let finalDate = date;

    // Зберігаємо поточний час при виборі дати, якщо він є
    if (props.time && selectedDate) {
        finalDate = finalDate.clone().hour(selectedDate.hour()).minute(selectedDate.minute());
    } else if (props.time && inputTime.length === 5) {
      const [hh, mm] = inputTime.split(':');
      finalDate = finalDate.clone().hour(hh).minute(mm);
    } else if (!props.time) {
        finalDate = finalDate.startOf('day'); // Обнуляємо час для дати без часу
    }
    
    setSelectedDate(finalDate);

    const formatted = props.time
      ? finalDate.format('DD.MM.YYYY HH:mm')
      : finalDate.format('DD.MM.YYYY');

    setInputValue(finalDate.format('DD.MM.YYYY'));
    if (props.time) setInputTime(finalDate.format('HH:mm'));

    setIsCalendarOpen(false);
    if(formatted == props.value){
      }else{
        props.onChange?.({
          target: { id: props.id, value: formatted }
        });
      }
  };

  const handleBlur = (e) => {

    if (!props.time) {
      if (inputValue.length === 10) {
        validateAndSetDate(inputValue);
      }
      props.onBlur?.(1, e);
      return;
    }

  
    if (inputValue.length === 10) {
        validateAndSetDate(inputValue); 
    } 
    else if (selectedDate && inputTime.length === 5) {
      const [hh, mm] = inputTime.split(':');
      
      const newDate = selectedDate.clone()
        .hour(parseInt(hh, 10))
        .minute(parseInt(mm, 10))
        .second(0)
        .millisecond(0);
      
      setSelectedDate(newDate); 
      if(newDate.format('DD.MM.YYYY HH:mm') == props.value){
        return;
      }else{
        props.onChange?.({
          target: { id: props.id, value: newDate.format('DD.MM.YYYY HH:mm') }
        });
      }
      
    }
  };


  const handlePaste = (e) => {
    e.preventDefault();

    let pasteText = e.clipboardData.getData("text");

    if (/[a-zA-Zа-яА-Я]/.test(pasteText)) return;

    pasteText = pasteText.replace(/[^0-9./-]/g, "");

    let parts = pasteText.split(/[^0-9]+/).filter(Boolean);

    if (parts[0] && parts[0].length === 1) parts[0] = parts[0].padStart(2, "0");
    if (parts[1] && parts[1].length === 1) parts[1] = parts[1].padStart(2, "0");

    let formattedDate = parts.join(".");
    setInputValue(formatInputValue(formattedDate));
  };

  const handleTimePaste = (e) => {
    e.preventDefault();
    let t = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
    if (t.length >= 3) t = t.slice(0, 2) + ":" + t.slice(2, 4);
    setInputTime(formatTime(t));
  };

  useEffect(() => {
    inputRef.current?.addEventListener("paste", handlePaste);
    timeRef.current?.addEventListener("paste", handleTimePaste);

    return () => {
      if (inputRef.current) inputRef.current.removeEventListener("paste", handlePaste);
      if (timeRef.current) timeRef.current.removeEventListener("paste", handleTimePaste);
    };
  }, []);

  const changeMonth = (direction) => {
    setCurrentMonth(currentMonth.add(direction, 'month'));
  };

  const daysInMonth = currentMonth.daysInMonth();
  
  const firstDayOfMonthIndex = currentMonth.startOf('month').day();
  const firstDayOfMonth = (firstDayOfMonthIndex + 6) % 7; 

  const monthName = t(currentMonth.format('MMMM'));

  return (
    <div className="date-picker">
      <input
        ref={inputRef}
        className="filterBlockInput"
        type="text"
        id={props.id}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        placeholder={t("dd_mm_yyyy")}
        maxLength={10}
      />

      {props.time && (
        <input
          ref={timeRef}
          className="filterBlockInput timeInput"
          type="text"
          value={inputTime}
          onChange={handleTimeChange}
          onBlur={handleBlur}
          placeholder="HH:MM"
          maxLength={5}
        />
      )}

      {props.icon && (
        <div ref={iconRef} className="dateIcon" onClick={toggleCalendar}>
          <Icon type='calendar' />
        </div>
      )}

      {isCalendarOpen && (
        <div ref={calendarRef} className={`calendar ${props.alignRight ? 'alignRight' : ''}`}>
          <div className="header prFlexRow">
            <button onClick={() => changeMonth(-1)}>
              <Icon type='pagArrowLeft' />
            </button>
            <span>{`${monthName} ${currentMonth.format('YYYY')}`}</span>
            <button onClick={() => changeMonth(1)}>
              <Icon type='pagArrow' />
            </button>
          </div>

          <div className="days">
            {[t('Mo'), t('Tu'), t('We'), t('Th'), t('Fr'), t('Sa'), t('Su')].map((d) => (
              <div key={d} className="day-name">{d}</div>
            ))}

            {Array.from({ length: firstDayOfMonth }).map((_, i) => (
              <div key={i} className="empty-day" />
            ))}

            {Array.from({ length: daysInMonth }).map((_, index) => {
              const day = index + 1;
              
              const dateToCheck = currentMonth.clone().date(day).startOf('day');
              const isSelected = selectedDate && dateToCheck.isSame(selectedDate.startOf('day'));

              return (
                <div
                  key={day}
                  className={isSelected ? "day active" : "day"}
                  onClick={() => handleDateSelect(currentMonth.clone().date(day))}
                >
                  {day}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;